<template>
  <div class="we-question-card" :class="{ active: active }" @click="$emit('select', question.id)">
    <div class="we-q-head">
      <div class="we-q-head-left">
        <span class="we-drag-handle" title="拖动排序">⋮⋮</span>
        <span class="we-q-index">{{ index }}</span>
        <i :class="typeMeta.icon"></i>
        <span>{{ typeMeta.name }}</span>
      </div>
      <div class="we-q-head-right" @click.stop>
        <el-button type="text" class="we-move-btn" icon="el-icon-arrow-up" :disabled="index <= 1"
          @click="$emit('move', question.id, -1)" />
        <el-button type="text" class="we-move-btn" icon="el-icon-arrow-down" :disabled="isLast"
          @click="$emit('move', question.id, 1)" />
        <span class="we-score-label">分值</span>
        <el-input-number v-model="question.score" :min="0" :controls="false" size="mini" class="we-score-input"
          placeholder="0" />
        <el-button type="text" class="we-del-btn" icon="el-icon-delete" @click="$emit('remove', question.id)" />
      </div>
    </div>

    <div class="we-q-body" @focusin="$emit('select', question.id)">
      <el-form label-position="top" size="small" hide-required-asterisk>
        <el-form-item required>
          <div slot="label" class="we-title-label" @click.stop>
            <span>题目</span>
            <el-button v-if="!question.titleUrl" type="text" icon="el-icon-picture-outline" class="we-title-insert-btn"
              @click="showTitleUpload = true">
              插入图片
            </el-button>
            <el-button v-else type="text" icon="el-icon-delete" class="we-title-img-del" @click="clearTitleImg">
              删除图片
            </el-button>
          </div>
          <el-input v-model="question.title" type="textarea" :rows="2" placeholder="请输入题目内容" maxlength="2000"
            show-word-limit />
          <div v-if="question.titleUrl" class="we-title-img" @click.stop>
            <el-image :src="downloadURL + question.titleUrl" fit="contain"
              :preview-src-list="[downloadURL + question.titleUrl]" class="we-title-img-el" />
          </div>
        </el-form-item>

        <!-- 问答题 -->
        <template v-if="question.type === 'qa'">
          <el-form-item label="参考答案">
            <el-input v-model="question.answer" placeholder="请输入参考答案（选填）" />
          </el-form-item>
        </template>

        <!-- 判断题 -->
        <template v-if="question.type === 'tf'">
          <el-form-item required>
            <span slot="label">正确答案</span>
            <el-radio-group v-model="question.answer">
              <el-radio label="正确">正确</el-radio>
              <el-radio label="错误">错误</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>

        <!-- 单选 / 多选 -->
        <template v-if="question.type === 'single' || question.type === 'multi'">
          <el-form-item required>
            <span slot="label">正确答案（选中为正确答案）</span>
            <div class="we-options">
              <template v-if="question.type === 'single'">
                <div v-for="(opt, i) in question.options" :key="opt.value" class="we-option-row">
                  <el-radio v-model="question.answer" :label="opt.value">{{ opt.value }}</el-radio>
                  <el-input v-model="opt.label" :placeholder="`选项 ${opt.value}`" />
                  <el-button v-if="question.options.length > 2" type="text" icon="el-icon-remove-outline"
                    @click="removeOption(i)" />
                </div>
              </template>
              <el-checkbox-group v-else v-model="question.answer">
                <div v-for="(opt, i) in question.options" :key="opt.value" class="we-option-row">
                  <el-checkbox :label="opt.value">{{ opt.value }}</el-checkbox>
                  <el-input v-model="opt.label" :placeholder="`选项 ${opt.value}`" />
                  <el-button v-if="question.options.length > 2" type="text" icon="el-icon-remove-outline"
                    @click="removeOption(i)" />
                </div>
              </el-checkbox-group>
              <el-button v-if="question.options.length < 8" type="text" icon="el-icon-circle-plus-outline"
                @click="addOption">添加选项</el-button>
            </div>
          </el-form-item>
        </template>

        <!-- 附件题 -->
        <template v-if="question.type === 'attach'">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="上传模板">
                <div v-if="question.templateMd5" class="we-template-file">
                  <a :href="downloadURL + question.templateMd5" target="_blank">{{ question.templateName || '已上传模板'
                    }}</a>
                  <el-button type="text" icon="el-icon-delete" @click="clearTemplate" />
                </div>
                <el-button v-else type="primary" plain size="small" icon="el-icon-upload2" @click="showUpload = true">
                  上传习题模板
                </el-button>
                <p class="we-tip">学员可下载模板作答后提交</p>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item required>
                <span slot="label">提交作业格式限制</span>
                <div class="we-format-grid">
                  <el-checkbox-group v-model="question.formats" @change="onFormatsChange">
                    <el-checkbox v-for="fmt in formatList" :key="fmt.value" :label="fmt.value">{{ fmt.label
                      }}</el-checkbox>
                  </el-checkbox-group>
                </div>
                <p class="we-tip">「不限制」与其它格式互斥</p>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="能力标准">
              <el-cascader v-model="question.powerValue" :options="powerlist"
                :props="{ multiple: true, label: 'title', value: 'id' }" filterable clearable style="width: 100%"
                @change="onPowerChange" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="答案解析">
              <el-input v-model="question.resolve" placeholder="请输入答案解析内容" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <el-drawer title="上传习题模板" :visible.sync="showUpload" size="480px" :wrapper-closable="false">
      <ossUploader v-if="showUpload" isInput="true" @closeMain="onUploadDone" />
    </el-drawer>
    <el-drawer title="上传题目图片" :visible.sync="showTitleUpload" size="480px" :wrapper-closable="false">
      <ossUploader v-if="showTitleUpload" isInput="true" @closeMain="onTitleUploadDone" />
    </el-drawer>
  </div>
</template>

<script>
  import { TYPE_META, ATTACH_FORMATS } from '../utils/questionTypes'

  const OPTION_KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

  export default {
    name: 'WorkEditorQuestionCard',
    props: {
      question: {
        type: Object,
        required: true
      },
      index: {
        type: Number,
        required: true
      },
      isLast: {
        type: Boolean,
        default: false
      },
      active: {
        type: Boolean,
        default: false
      },
      powerlist: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        showUpload: false,
        showTitleUpload: false,
        formatList: ATTACH_FORMATS
      }
    },
    computed: {
      typeMeta() {
        return TYPE_META[this.question.type] || { name: '题目', icon: 'el-icon-document' }
      }
    },
    methods: {
      addOption() {
        const next = OPTION_KEYS[this.question.options.length]
        if (!next) return
        this.question.options.push({ label: '', value: next })
      },
      removeOption(i) {
        const removed = this.question.options[i]
        this.question.options.splice(i, 1)
        if (this.question.type === 'single' && this.question.answer === removed.value) {
          this.question.answer = ''
        }
        if (this.question.type === 'multi' && Array.isArray(this.question.answer)) {
          this.question.answer = this.question.answer.filter((v) => v !== removed.value)
        }
      },
      onFormatsChange(vals) {
        const list = vals || []
        const last = list[list.length - 1]
        if (last === 'any') {
          this.$set(this.question, 'formats', ['any'])
          return
        }
        const next = list.filter((v) => v !== 'any')
        this.$set(this.question, 'formats', next.length ? next : ['any'])
      },
      onPowerChange(val) {
        if (!val || !val.length) {
          this.$set(this.question, 'power', [])
          return
        }
        const arr = val.map((item) => ({
          star: '',
          power: item[item.length - 1],
          powerVal: val
        }))
        this.$set(this.question, 'power', arr)
      },
      clearTemplate() {
        this.question.templateMd5 = ''
        this.question.templateName = ''
      },
      clearTitleImg() {
        this.question.titleUrl = ''
      },
      onUploadDone(val) {
        this.question.templateMd5 = val.filemd5
        this.question.templateName = val.name
        this.showUpload = false
        this.$message.success('模板已上传')
      },
      onTitleUploadDone(val) {
        this.question.titleUrl = val.filemd5 || ''
        this.showTitleUpload = false
        this.$message.success('题目图片已上传')
      }
    }
  }
</script>

<style lang="scss" scoped>
  .we-question-card {
    border: 1.5px solid #e5e6eb;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.25s;
    margin-bottom: 16px;

    &.active {
      border-color: #4f6ef7;
      // border-color: #409EFF;
      box-shadow: 0 0 0 2px rgba(79, 110, 247, 0.15);
    }
  }

  .we-q-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    // background: #409EFF;
    background: linear-gradient(135deg, #4f6ef7 0%, #3b57d9 100%);
    color: #fff;
  }

  .we-q-head-left {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
  }

  .we-drag-handle {
    cursor: grab;
    opacity: 0.85;
    letter-spacing: -1px;
    user-select: none;
  }

  .we-q-index {
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.25);
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .we-q-head-right {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .we-move-btn,
  .we-del-btn {
    color: #fff !important;
    padding: 4px !important;
    font-size: 14px;

    &:hover {
      opacity: 0.85;
    }

    &.is-disabled {
      opacity: 0.35;
    }
  }

  .we-score-label {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    margin-left: 4px;
  }

  .we-score-input {
    width: 72px;

    ::v-deep .el-input__inner {
      height: 30px;
      line-height: 30px;
      font-size: 15px;
      font-weight: 600;
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.4);
      color: #fff;
      text-align: center;
    }
  }

  .we-q-body {
    padding: 16px 20px 8px;
  }

  .we-title-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    line-height: 1;
  }

  .we-title-insert-btn {
    padding: 0 !important;
    font-size: 12px;
  }

  .we-title-img {
    margin-top: 8px;
  }

  .we-title-img-el {
    width: 160px;
    height: 120px;
    border-radius: 6px;
    border: 1px solid #e5e6eb;
    background: #f7f8fa;
  }

  .we-title-img-del {
    color: #f7607d !important;
    padding: 0 !important;
    font-size: 12px;
  }

  .we-required {
    color: #f7607d;
    margin-right: 2px;
  }

  .we-options {
    width: 100%;
  }

.we-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  ::v-deep .el-radio,
  ::v-deep .el-checkbox {
    margin-right: 0;
  }
}

.we-format-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;

    ::v-deep .el-checkbox {
      margin-right: 0;
    }
  }

  .we-template-file {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 6px;

    a {
      color: #1d2129;
      text-decoration: none;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .we-tip {
    margin: 4px 0 0;
    font-size: 12px;
    color: #86909c;
    line-height: 1.5;
  }
</style>
